import * as THREE from 'three';
import type { LoopHandle } from '../audio/AudioEngine';
import { CONFIG } from '../config';
import { isTouch } from '../core/device';
import { rideDef, type RideId } from '../content/rides';
import { CarController } from '../entities/CarController';
import type { GameServices } from './services';

/** Getting in, driving, and getting out of whatever's in your driveway. */
export class Driving {
  active = false;
  readonly car: CarController;
  private engine: LoopHandle | null = null;
  private colliders: number[] = [];
  private trafficCooldown = 0;
  private readonly tmp = new THREE.Vector3();

  constructor(private readonly s: GameServices) {
    this.car = new CarController(s.world.rideRoot);
  }

  /** What's in your driveway: null if you sold the Camaro and never bought anything. */
  currentRide(): RideId | null {
    const st = this.s.store.state;
    const id = rideDef(st.ride).id;
    return id === 'camaro' && st.flags.camaro_sold ? null : id;
  }

  /** The Camaro needs fixing first; everything from Cooter's runs. Mostly. */
  get drivable(): boolean {
    const id = this.currentRide();
    return id !== null && (id !== 'camaro' || this.s.store.state.camaro >= 100);
  }

  get position(): THREE.Vector3 {
    return this.car.position;
  }

  /** The driver's eyes, for the first-person view (the car faces +x, the driver sits on the left). */
  eye(out: THREE.Vector3): THREE.Vector3 {
    out.set(-0.15, this.car.stats.seat * 0.9 + 0.14, -0.33);
    return this.car.root.localToWorld(out);
  }

  /** Re-park whatever's in your driveway (new game, load, bought or swapped a ride). */
  reset(): void {
    this.stop(true);
    const id = this.currentRide();
    const { world, store } = this.s;
    world.rideRoot.visible = id !== null;
    if (id) {
      world.setRide(id, id === 'camaro' && store.state.camaro < 100);
      this.car.stats = rideDef(id).stats;
    }
    const c = this.s.home().camaro;
    this.car.reset(c.x, c.z, c.heading);
    this.clearColliders();
    if (id) this.placeColliders();
  }

  /** Two circles approximate the parked car for collision. */
  placeColliders(): void {
    this.clearColliders();
    const f = this.car.forward(this.tmp);
    const p = this.car.position;
    const { axle, radius } = this.car.stats;
    for (const off of [axle, -axle])
      this.colliders.push(this.s.world.collision.addCircle(p.x + f.x * off, p.z + f.z * off, radius * 0.92));
  }

  clearColliders(): void {
    for (const id of this.colliders) this.s.world.collision.remove(id);
    this.colliders = [];
  }

  start(): void {
    const { player, store, audio, cameraRig } = this.s;
    if (player.carrying || player.pushing) {
      store.toast('Put that down first. Hands at ten and two.', 'bad', ':car:');
      return;
    }
    this.active = true;
    player.hidden = true;
    this.clearColliders();
    this.engine = audio.loop('engine');
    this.s.sfx('horn');
    store.incr('drives');
    cameraRig.distance = 10;
    store.toast(
      isTouch()
        ? 'VROOOM. Joystick to drive, hold :hop: for the handbrake, :air-horn: to honk, E to get out.'
        : 'VROOOM. W/S gas & brake, A/D steer, Space handbrake, H honk, E to get out.',
      'good',
      ':car:',
    );
  }

  stop(force = false): void {
    if (!this.active) return;
    if (!force && Math.abs(this.car.speed) > 3) {
      this.s.store.toast('Slow down first, Dale Jr.', 'info', ':car:');
      return;
    }
    this.active = false;
    this.car.speed = 0;
    this.engine?.stop();
    this.engine = null;
    const f = this.car.forward(this.tmp);
    const out = this.car.stats.radius + 1.05;
    const side = { x: this.car.position.x - f.z * out, z: this.car.position.z + f.x * out };
    this.s.world.collision.resolve(side, CONFIG.player.radius);
    this.s.player.teleport(side.x, side.z);
    this.s.player.hidden = false;
    this.placeColliders();
    this.s.cameraRig.distance = 8;
  }

  update(dt: number): void {
    const { input, world, player, effects, store, ui } = this.s;
    this.trafficCooldown -= dt;
    const axis = input.moveAxis();
    const res = this.car.update(
      dt,
      {
        throttle: axis.y,
        steer: axis.x,
        brake: input.isDown('Space'),
        mud: world.mudAt(this.car.position.x, this.car.position.z),
      },
      world.collision,
      world.ride,
    );
    const pos = this.car.position;
    player.position.set(pos.x, 0, pos.z);
    player.syncTransform();
    this.engine?.set(Math.abs(this.car.speed) / CONFIG.car.maxSpeed);
    if (res.bumped) {
      this.s.sfx('thud');
      effects.shake(0.3, 0.25);
    }
    if (res.donut) {
      store.incr('donuts');
      store.addClout(3);
      ui.hud.float(':donut: SICK DONUTS', player.headPosition(), '#ffd400');
    }
    if (res.drifting || Math.abs(this.car.speed) > 12) {
      const f = this.car.forward(this.tmp);
      world.smoke.spawn({
        x: pos.x - f.x * 2,
        y: 0.3,
        z: pos.z - f.z * 2,
        vx: (Math.random() - 0.5) * 2,
        vy: 0.6,
        vz: (Math.random() - 0.5) * 2,
        life: 1.2,
        size: 0.8,
        grow: 3,
        color: '#b8a68a',
      });
    }
    if (input.wasPressed('KeyH')) this.s.sfx('horn');
    const hit = effects.trafficHit(pos.x, pos.z, 1.1);
    if (hit && this.trafficCooldown <= 0) {
      this.trafficCooldown = 1.5;
      const semi = hit.kind === 'semi';
      this.car.speed *= semi ? -1.2 : -0.6;
      this.s.sfx(semi ? 'semiHorn' : 'horn');
      this.s.sfx('thud');
      effects.shake(semi ? 1.1 : 0.5, semi ? 0.7 : 0.4);
      if (semi) {
        store.apply({ stats: { health: -20 } });
        store.incr('semiHits');
      }
      store.toast(
        semi
          ? 'You played chicken with an 18-wheeler and lost. Your fillings are still ringing.'
          : 'Fender bender on Highway 41! The other guy flipped you a bird and kept going.',
        'bad',
        semi ? ':semi:' : ':boom:',
      );
    }
  }
}
